import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { orderService } from '@/services/order.service';
import { Order } from '@/types/order.types';
import { formatPrice } from '@/utils/formatters';
import { ClipboardDocumentListIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

export const MesCommandes = () => {
  const { t, i18n } = useTranslation();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOrders = async () => {
      try {
        const data = await orderService.getAll({ limit: 50 });
        setOrders(data);
      } catch (err: any) {
        setError(err?.message || t('clientPages.orders.loadError'));
      } finally {
        setLoading(false);
      }
    };

    loadOrders();
  }, [t]);

  const formatDate = (value: string) =>
    new Date(value).toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });

  return (
    <>
      <Navbar />
      <main className="client-page">
        <div className="client-page-container">
          <h1 className="gtitle client-page-title">
            <span className="gtitle-light">{t('clientPages.orders.titleLight')}</span>{' '}
            <span className="gtitle-strong">{t('clientPages.orders.titleStrong')}</span>
          </h1>

          {loading && <p className="client-page-muted">{t('clientPages.orders.loading')}</p>}

          {error && (
            <div className="client-page-alert client-page-alert-error" role="alert">
              {error}
            </div>
          )}

          {!loading && !error && orders.length === 0 && (
            <div className="client-page-empty">
              <ClipboardDocumentListIcon className="client-page-empty-icon" aria-hidden="true" />
              <p>{t('clientPages.orders.empty')}</p>
              <Link to="/boutique" className="btn btn-primary">
                {t('clientPages.orders.shopCta')}
              </Link>
            </div>
          )}

          {!loading && !error && orders.length > 0 && (
            <ul className="client-orders-list">
              {orders.map((order) => (
                <li key={order.id} className="client-order-card">
                  <div className="client-order-card-header">
                    <div>
                      <p className="client-order-number">#{order.order_number}</p>
                      <p className="client-page-muted">{formatDate(order.created_at)}</p>
                    </div>
                    <p className="client-order-total">{formatPrice(Number(order.total))}</p>
                  </div>
                  <div className="client-order-badges">
                    <span className={`client-badge client-badge-${order.order_status}`}>
                      {t(`orderTrackingPage.status.${order.order_status}`)}
                    </span>
                    <span className={`client-badge client-badge-payment-${order.payment_status}`}>
                      {t(`clientPages.orders.payment.${order.payment_status}`)}
                    </span>
                  </div>
                  <Link
                    to={`/order-tracking?orderNumber=${encodeURIComponent(order.order_number)}`}
                    className="client-order-link"
                  >
                    <MagnifyingGlassIcon className="client-order-link-icon" aria-hidden="true" />
                    {t('clientPages.orders.track')}
                  </Link>
                </li>
              ))}
            </ul>
          )}

          <p className="client-page-muted client-page-footnote">
            {t('clientPages.orders.trackHint')}{' '}
            <Link to="/order-tracking">{t('navbar.trackOrder')}</Link>
          </p>
        </div>
      </main>
      <Footer />
    </>
  );
};
